import React from 'react'
import { Card } from 'react-bootstrap'
import './company-detail.css'

/**
 * The CompanyInformation component in React displays company metadata in a structured grid format with
 * improved handling of data and rendering.
 * @param props - The `props` object in the `CompanyInformation` component contains various optional
 * properties that can be passed to the component for displaying company metadata. Here is a breakdown
 * of the expected props:
 * @returns The `CompanyInformation` component is being returned. It is a presentational component that
 * displays company metadata in a grid of rows. The component renders various company details such as
 * description, legal name, entity type, operational status, funding information, location details,
 * industry, and categories. The component uses helper functions to handle rendering of different types
 * of data and ensures proper display of information even when values are missing.
 */

function CompanyInformation(props) {
    const {
        description,
        legalName,
        formerLegalName,
        entityType,
        isActive,
        foundedDate,
        operationalDuration,
        shutdownDate,
        lastFinancingRound,
        totalRaisedCurrency,
        totalRaised,
        totalRaisedSymbol,
        lastFinancingDate,
        noOfFundingRounds,
        investors,
        noOfEmployeesMin,
        noOfEmployeesMax,
        stageOfDevelopment,
        businessModelType,
        defensibilityMoat,
        city,
        state,
        country,
        industry,
        categories
    } = props || {}

    const renderText = (value) => {
        if (value === null || value === undefined) return '-'
        if (typeof value === 'string' && value.trim() === '') return '-'
        return value
    }

    const renderArray = (arr) => {
        if (!Array.isArray(arr) || arr.length === 0) return '-'
        const cleaned = arr.filter((v) => v !== null && v !== undefined && (typeof v !== 'string' || v.trim() !== ''))
        return cleaned.length === 0 ? '-' : cleaned.join(', ')
    }

    const renderEmployees = (min, max) => {
        const hasMin = min !== null && min !== undefined && String(min).trim() !== ''
        const hasMax = max !== null && max !== undefined && String(max).trim() !== ''
        if (!hasMin && !hasMax) return '-'
        if (hasMin && hasMax) return `${min} - ${max}`
        return hasMin ? `${min}` : `${max}`
    }

    const renderOperationalStatus = (flag) => {
        if (flag === true) return 'Active'
        if (flag === false) return 'Inactive'
        return '-'
    }

    const renderOperationalDuration = (value) => {
        if (value === null || value === undefined || value === '') return '-'
        return `${value} ${value === 1 ? 'year' : 'years'}`
    }

    const renderTotalRaised = (currency, amount, symbol) => {
        if (amount === null || amount === undefined || amount === '') return '-'
        const parts = []
        if (currency) parts.push(currency)
        parts.push(amount)
        if (symbol) parts.push(symbol)
        return parts.join(' ')
    }

    return (
        <Card.Body id="companyinformation">
            <div>
                <p className="companydetailtitle">Description</p>
                <p className="companydetaildata">{renderText(description)}</p>
            </div>

            <div className="companydetailrows">
                <div className="companydetaildiv">
                    <p className="companydetailtitle">Legal Name</p>
                    <p className="companydetaildata">{renderText(legalName)}</p>
                </div>
                <div className="companydetaildiv">
                    <p className="companydetailtitle">Former Legal Name(s)</p>
                    <p className="companydetaildata">{renderArray(formerLegalName)}</p>
                </div>
                <div className="companydetaildiv">
                    <p className="companydetailtitle">Company Reputation</p>
                    <p className="companydetaildata">-</p>
                </div>
            </div>

            <div className="companydetailrows">
                <div className="companydetaildiv">
                    <p className="companydetailtitle">Entity Type</p>
                    <p className="companydetaildata">{renderText(entityType)}</p>
                </div>
                <div className="companydetaildiv">
                    <p className="companydetailtitle">Ownership Status</p>
                    <p className="companydetaildata">-</p>
                </div>
                <div className="companydetaildiv">
                    <p className="companydetailtitle">Operational Status</p>
                    <p className="companydetaildata">{renderOperationalStatus(isActive)}</p>
                </div>
            </div>

            <div className="companydetailrows">
                <div className="companydetaildiv">
                    <p className="companydetailtitle">Founded Date</p>
                    <p className="companydetaildata">{renderText(foundedDate)}</p>
                </div>
                <div className="companydetaildiv">
                    <p className="companydetailtitle">Operational Duration</p>
                    <p className="companydetaildata">{renderOperationalDuration(operationalDuration)}</p>
                </div>
                <div className="companydetaildiv">
                    <p className="companydetailtitle">Shutdown Date</p>
                    <p className="companydetaildata">{renderText(shutdownDate)}</p>
                </div>
            </div>

            <div className="companydetailrows">
                <div className="companydetaildiv">
                    <p className="companydetailtitle">Last Financing Round</p>
                    <p className="companydetaildata">{renderText(lastFinancingRound)}</p>
                </div>
                <div className="companydetaildiv">
                    <p className="companydetailtitle">Total Raised</p>
                    <p className="companydetaildata">{renderTotalRaised(totalRaisedCurrency, totalRaised, totalRaisedSymbol)}</p>
                </div>
                <div className="companydetaildiv">
                    <p className="companydetailtitle">Last Financing Date</p>
                    <p className="companydetaildata">{renderText(lastFinancingDate)}</p>
                </div>
            </div>

            <div className="companydetailrows">
                <div className="companydetaildiv">
                    <p className="companydetailtitle">No. of Funding Round(s)</p>
                    <p className="companydetaildata">{renderText(noOfFundingRounds)}</p>
                </div>
                <div className="companydetaildiv">
                    <p className="companydetailtitle">No. of Investor(s)</p>
                    <p className="companydetaildata">{Array.isArray(investors) ? investors.length : '-'}</p>
                </div>
                <div className="companydetaildiv">
                    <p className="companydetailtitle">No. of Employees</p>
                    <p className="companydetaildata">{renderEmployees(noOfEmployeesMin, noOfEmployeesMax)}</p>
                </div>
            </div>

            <div className="companydetailrows">
                <div className="companydetaildiv">
                    <p className="companydetailtitle">Total Revenue</p>
                    <p className="companydetaildata">-</p>
                </div>
                <div className="companydetaildiv">
                    <p className="companydetailtitle">Post-money Valuation</p>
                    <p className="companydetaildata">-</p>
                </div>
                <div className="companydetaildiv">
                    <p className="companydetailtitle">Market Capitalization</p>
                    <p className="companydetaildata">-</p>
                </div>
            </div>

            <div className="companydetailrows">
                <div className="companydetaildiv">
                    <p className="companydetailtitle">Stage of Development</p>
                    <p className="companydetaildata">{renderText(stageOfDevelopment)}</p>
                </div>
                <div className="companydetaildiv">
                    <p className="companydetailtitle">Transactional Model Type</p>
                    <p className="companydetaildata">{renderText(businessModelType)}</p>
                </div>
                <div className="companydetaildiv">
                    <p className="companydetailtitle">Defensibility Moats</p>
                    <p className="companydetaildata">{renderArray(defensibilityMoat)}</p>
                </div>
            </div>

            <div className="companydetailrows">
                <div className="companydetaildiv">
                    <p className="companydetailtitle">City</p>
                    <p className="companydetaildata">{renderText(city)}</p>
                </div>
                <div className="companydetaildiv">
                    <p className="companydetailtitle">State/Province</p>
                    <p className="companydetaildata">{renderText(state)}</p>
                </div>
                <div className="companydetaildiv">
                    <p className="companydetailtitle">Country</p>
                    <p className="companydetaildata">{renderText(country)}</p>
                </div>
            </div>

            <div>
                <p className="companydetailtitle">Industry</p>
                <p className="companydetaildata">{renderText(industry)}</p>
            </div>

            <div>
                <p className="companydetailtitle">Categories</p>
                <p className="companydetaildata">{renderArray(categories)}</p>
            </div>
        </Card.Body>
    )
}

export default CompanyInformation;